import React, { useState, useEffect, useCallback } from 'react';
import api, { getLeaveBalances, getLeaveConfig } from '../api';
import { useAuth } from '../AuthContext';

const LEGACY_TYPES = ['vacation', 'sick', 'personal', 'unpaid']; // fallback when no leave types configured
const STATUS_COLORS = { pending: '#d97706', approved: '#059669', rejected: '#dc2626' };
const card = { background: 'var(--glass)', backdropFilter: 'var(--blur)', WebkitBackdropFilter: 'var(--blur)', border: '1px solid var(--glass-border)', borderRadius: 12, padding: 20, marginBottom: 16 };
const inp = { width: '100%', padding: '9px 11px', border: '1px solid var(--glass-border)', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-2)', margin: '12px 0 5px' };
const today = () => new Date().toISOString().slice(0, 10);
const daysBetween = (s, e) => (s && e && e >= s) ? Math.round((new Date(e) - new Date(s)) / 86400000) + 1 : 0;

export default function Leave() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState([]);
  const [allowPast, setAllowPast] = useState(false);
  const [form, setForm] = useState({ leave_type_id: '', type: 'vacation', start_date: '', end_date: '', reason: '' });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    // This page is self-service: show only the signed-in user's own requests
    // (admins review everyone under Manage Leave).
    try {
      const params = user?.employee_id ? { employee_id: user.employee_id } : {};
      const { data } = await api.get('/leave', { params });
      setRows(data);
    } catch {}
    if (user?.employee_id) {
      try { setBalances(await getLeaveBalances()); } catch {}
    }
    try { const cfg = await getLeaveConfig(); setAllowPast(!!cfg.allow_past); } catch {}
  }, [user?.employee_id]);
  useEffect(() => { load(); }, [load]);

  const usingTypes = balances.length > 0;
  const selectedBal = usingTypes ? balances.find(b => b.id === form.leave_type_id) : null;
  const reqDays = daysBetween(form.start_date, form.end_date);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setMsg('');
    if (usingTypes && !form.leave_type_id) { setErr('Please choose a leave type.'); return; }
    try {
      const payload = usingTypes
        ? { leave_type_id: form.leave_type_id, start_date: form.start_date, end_date: form.end_date, reason: form.reason }
        : { type: form.type, start_date: form.start_date, end_date: form.end_date, reason: form.reason };
      await api.post('/leave', payload);
      setMsg('Leave request submitted.');
      setForm({ leave_type_id: '', type: 'vacation', start_date: '', end_date: '', reason: '' });
      load();
    } catch (e) { setErr(e.response?.data?.error || 'Failed to submit'); }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)', margin: '0 0 20px' }}>Apply for Leave</h1>

      {/* Balance chips */}
      {user?.employee_id && balances.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {balances.map(b => (
            <div key={b.id} style={{ ...card, padding: '10px 14px', margin: 0, minWidth: 130 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontWeight: 600 }}>{b.name}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text)' }}>
                {b.unlimited ? '∞' : b.remaining}<span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>{b.unlimited ? ' unlimited' : ` / ${b.allowed}`}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{b.unlimited ? b.category : `${b.used} used${b.carried ? ` · +${b.carried} carried` : ''}`}</div>
            </div>
          ))}
        </div>
      )}

      {user?.employee_id && (
        <form onSubmit={submit} style={card}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>New request</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={lbl}>Type</label>
              {usingTypes ? (
                <select value={form.leave_type_id} onChange={e => setForm(f => ({ ...f, leave_type_id: e.target.value }))} style={inp} required>
                  <option value="">Select…</option>
                  {balances.map(b => <option key={b.id} value={b.id}>{b.name}{b.unlimited ? '' : ` (${b.remaining} left)`}</option>)}
                </select>
              ) : (
                <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inp}>
                  {LEGACY_TYPES.map(t => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={lbl}>From</label>
              <input type="date" min={allowPast ? undefined : today()} value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inp} required />
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label style={lbl}>To</label>
              <input type="date" min={form.start_date || (allowPast ? undefined : today())} value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={inp} required />
            </div>
          </div>
          {reqDays > 0 && (
            <div style={{ fontSize: 12, color: selectedBal && !selectedBal.unlimited && reqDays > selectedBal.remaining ? '#dc2626' : 'var(--muted)', marginTop: 8 }}>
              {reqDays} day{reqDays > 1 ? 's' : ''} requested
              {selectedBal && !selectedBal.unlimited && ` · ${selectedBal.remaining} available${reqDays > selectedBal.remaining ? ' — exceeds balance' : ''}`}
            </div>
          )}
          <label style={lbl}>Reason (optional)</label>
          <input value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} style={inp} placeholder="e.g. Family trip" />
          {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{err}</div>}
          {msg && <div style={{ color: '#16a34a', fontSize: 13, marginTop: 10 }}>{msg}</div>}
          <button type="submit" disabled={selectedBal && !selectedBal.unlimited && reqDays > selectedBal.remaining}
            style={{ marginTop: 14, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (selectedBal && !selectedBal.unlimited && reqDays > selectedBal.remaining) ? 0.5 : 1 }}>Submit Request</button>
        </form>
      )}

      <div style={card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 12 }}>My requests</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--hairline)' }}>
              {['Type', 'From', 'To', 'Days', 'Reason', 'Status'].map(h => (
                <th key={h} style={{ padding: '9px 10px', textAlign: 'left', color: 'var(--text-2)', fontWeight: 600, borderBottom: '1px solid var(--glass-border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--hairline)' }}>
                <td style={{ padding: '9px 10px', textTransform: 'capitalize' }}>{r.type}</td>
                <td style={{ padding: '9px 10px' }}>{r.start_date}</td>
                <td style={{ padding: '9px 10px' }}>{r.end_date}</td>
                <td style={{ padding: '9px 10px' }}>{r.days || '—'}</td>
                <td style={{ padding: '9px 10px', color: 'var(--muted)' }}>{r.reason || '—'}</td>
                <td style={{ padding: '9px 10px' }}>
                  <span style={{ background: STATUS_COLORS[r.status] + '20', color: STATUS_COLORS[r.status], padding: '2px 9px', borderRadius: 12, fontWeight: 600, fontSize: 11, textTransform: 'capitalize' }}>{r.status}</span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--muted)', padding: 28 }}>No leave requests yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
